import React from 'react';
import CodeBlock from '../documentation/CodeBlock';

const QuickStartContent = ({ step }) => {
    if (!step) return null;

    const renderContent = () => {
        switch (step.id) {
            case 'overview':
                return <OverviewContent />;
            case 'prerequisites':
                return <PrerequisitesContent />;
            case 'authentication':
                return <AuthenticationContent />;
            case 'first-payment':
                return <FirstPaymentContent />;
            case 'webhooks':
                return <WebhooksContent />;
            case 'deployment':
                return <DeploymentContent />;
            default:
                return null;
        }
    };

    return (
        <div className="p-8">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center text-2xl text-white">
                        {step.icon}
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{step.title}</h1>
                        <div className="flex items-center space-x-3 mt-1">
                            <span className="text-amber-600 font-medium">Durée : {step.duration}</span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-600">Difficulté : {step.difficulty}</span>
                        </div>
                    </div>
                </div>
            </div>

            {renderContent()}
        </div>
    );
};

// Sous-composants pour chaque étape
const OverviewContent = () => (
    <div className="space-y-6">
        <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed">
                Ce guide vous accompagne pas à pas dans l'intégration de Netikash. En moins de 15 minutes,
                vous serez capable de recevoir vos premiers paiements.
            </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-amber-50 rounded-xl border border-amber-200">
                <div className="text-2xl mb-3">🔐</div>
                <h3 className="font-semibold text-amber-800 mb-2">Authentification</h3>
                <p className="text-amber-700 text-sm">Obtenez votre token d'accès</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-200">
                <div className="text-2xl mb-3">💳</div>
                <h3 className="font-semibold text-blue-800 mb-2">Paiement</h3>
                <p className="text-blue-700 text-sm">Créez une demande de paiement</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-xl border border-green-200">
                <div className="text-2xl mb-3">✅</div>
                <h3 className="font-semibold text-green-800 mb-2">Vérification</h3>
                <p className="text-green-700 text-sm">Confirmez le statut du paiement</p>
            </div>
        </div>

        <div className="bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl p-6 text-white">
            <h3 className="font-bold text-lg mb-2">🎯 Objectif de ce guide</h3>
            <p>Implémenter un flux de paiement complet fonctionnel</p>
        </div>
    </div>
);

const PrerequisitesContent = () => (
    <div className="space-y-6">
        <StepNumber number={1} title="Compte Netikash Business" />
        <p className="text-gray-700">Assurez-vous d'avoir une organisation sur Netikash Gateaway.</p>

        <StepNumber number={2} title="Identifiants API" />
        <p className="text-gray-700">Récupérez votre Client ID et Client Secret depuis l'espace développeur.</p>

        <StepNumber number={3} title="Environnement de développement" />
        <p className="text-gray-700">Serveur avec HTTPS pour la production.</p>

        <CodeBlock
            code={`// Vérifiez vos identifiants
const CLIENT_ID = "votre_client_id";
const CLIENT_SECRET = "votre_client_secret";`}
            language="javascript"
        />
    </div>
);

const AuthenticationContent = () => (
    <div className="space-y-6">
        <CodeBlock
            code={`// Obtenir un token d'accès
async function getAccessToken() {
    const token = await netikash.auth.getToken();
    return token.access_token;
}`}
            language="javascript"
        />
    </div>
);

const FirstPaymentContent = () => (
    <div className="space-y-6">
        <CodeBlock
            code={`// Créer une demande de paiement
async function createPayment() {
    const payment = await netikash.payments.create({
        amount: 1000,
        currency: 'USD',
        reference: 'CMD-12345',
        redirect_uri: 'https://votresite.com/confirmation'
    });
    
    return payment.paymentUri;
}`}
            language="javascript"
        />
    </div>
);

const WebhooksContent = () => (
    <div className="space-y-6">
        <CodeBlock
            code={`// Configuration webhook
app.post('/webhooks/payment', (req, res) => {
    const signature = req.headers['x-netikash-signature'];
    const isValid = netikash.webhooks.verifySignature(req.body, signature);
    
    if (isValid) {
        // Traiter le paiement
        console.log('Paiement reçu:', req.body);
    }
});`}
            language="javascript"
        />
    </div>
);

const DeploymentContent = () => (
    <div className="space-y-6">
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-6 text-white">
            <h3 className="font-bold text-lg mb-2">🎉 Félicitations !</h3>
            <p>Votre intégration est maintenant complète. Passons en production.</p>
        </div>

        <StepNumber number={1} title="Changer l'environnement" />
        <CodeBlock
            code={`// Passer en production
const netikash = new Netikash({
    environment: 'production' // ← Changer ici
});`}
            language="javascript"
        />

        <StepNumber number={2} title="Tester exhaustivement" />
        <p className="text-gray-700">Vérifiez tous les scénarios de paiement avant la mise en production.</p>

        <StepNumber number={3} title="Monitorer les transactions" />
        <p className="text-gray-700">Utilisez le dashboard Netikash pour suivre vos transactions en temps réel.</p>
    </div>
);

// Composant helper pour les étapes numérotées
const StepNumber = ({ number, title }) => (
    <div className="flex items-center space-x-4">
        <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
            {number}
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
);

export default QuickStartContent;